import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { SettingsService } from '@/services/api'

// Request deduplication to prevent concurrent settings requests
let pendingSettingsRequest: ReturnType<typeof SettingsService.getSettings> | null = null

export interface AppSettings {
  theme: 'light' | 'dark'
  language: string
  timezone: string
  dateFormat: string
  timeFormat: string
  queryLimit: number
  autoComplete: boolean
  syntaxHighlighting: boolean
  showLineNumbers: boolean
  fontFamily: string
  fontSize: number
  keepExplorerExpandedState: boolean
  explorerCompactView: boolean
}

interface SettingsState {
  settings: AppSettings
  isLoading: boolean
  error: string | null
  fetchSettings: () => Promise<void>
  updateSetting: <K extends keyof AppSettings>(key: K, value: AppSettings[K]) => Promise<void>
  bulkUpdateSettings: (settings: Partial<AppSettings>) => Promise<void>
  applyTheme: (theme: AppSettings['theme']) => void
  resetSettings: () => void
}

const defaultSettings: AppSettings = {
  theme: 'light',
  language: 'en',
  timezone: 'UTC',
  dateFormat: 'YYYY-MM-DD',
  timeFormat: 'HH:mm:ss',
  queryLimit: 100,
  autoComplete: true,
  syntaxHighlighting: true,
  showLineNumbers: true,
  fontFamily: 'Monaco, "Lucida Console", monospace',
  fontSize: 12,
  keepExplorerExpandedState: false,
  explorerCompactView: false,
}

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set, get) => ({
      settings: defaultSettings,
      isLoading: false,
      error: null,

      fetchSettings: async () => {
        // If there's already a pending request, wait for it
        if (pendingSettingsRequest) {
          try {
            const response = await pendingSettingsRequest
            // Merge with default settings to ensure all properties exist
            const mergedSettings = { ...defaultSettings, ...response }
            
            // Convert 'auto' theme to 'light' if it exists (for backwards compatibility)
            const themeNormalized = mergedSettings.theme === 'auto' ? 'light' : mergedSettings.theme
            const coerced: AppSettings = { ...mergedSettings, theme: themeNormalized === 'dark' ? 'dark' : 'light' }
            set({ settings: coerced, isLoading: false })
            
            // Apply theme immediately
            get().applyTheme(coerced.theme)
            return
          } catch {
            // If the pending request failed, continue with a new request
          }
        }

        set({ isLoading: true, error: null })
        try {
          // Create and store the request promise
          const requestPromise = SettingsService.getSettings()
          pendingSettingsRequest = requestPromise

          const response = await requestPromise
          
          // Clear the pending request
          pendingSettingsRequest = null
          
          // Merge with default settings to ensure all properties exist
          const mergedSettings = { ...defaultSettings, ...response }
          const themeNormalized = mergedSettings.theme === 'auto' ? 'light' : mergedSettings.theme
          const coerced: AppSettings = { ...mergedSettings, theme: themeNormalized === 'dark' ? 'dark' : 'light' }
          set({ settings: coerced, isLoading: false })
          get().applyTheme(coerced.theme)
        } catch (error) {
          console.error('Failed to fetch settings:', error)
          // Clear the pending request on error
          pendingSettingsRequest = null
          set({ 
            error: error instanceof Error ? error.message : 'Failed to fetch settings',
            isLoading: false 
          })
        }
      },

      updateSetting: async (key, value) => {
        const currentSettings = get().settings
        const newSettings = { ...currentSettings, [key]: value }
        
        // Optimistically update local state
        set({ settings: newSettings, error: null })
        
        try {
          await SettingsService.updateSettings({ requestBody: newSettings })
          
          // Apply theme if theme was changed
          if (key === 'theme') {
            const theme = value === 'dark' ? 'dark' : 'light'
            get().applyTheme(theme)
          }
        } catch (error) {
          console.error('Failed to update setting:', error)
          // Revert local state on error
          set({ 
            settings: currentSettings,
            error: error instanceof Error ? error.message : 'Failed to update setting'
          })
          throw error
        }
      },

      bulkUpdateSettings: async (updates) => {
        const currentSettings = get().settings
        const newSettings = { ...currentSettings, ...updates }
        
        // Optimistically update local state
        set({ settings: newSettings, error: null })
        
        try {
          await SettingsService.updateSettings({ requestBody: newSettings })
          
          // Apply theme if theme was changed
          if (updates.theme) {
            const theme = updates.theme === 'dark' ? 'dark' : 'light'
            get().applyTheme(theme)
          }
        } catch (error) {
          console.error('Failed to update settings:', error)
          // Revert local state on error
          set({ 
            settings: currentSettings,
            error: error instanceof Error ? error.message : 'Failed to update settings'
          })
          throw error
        }
      },

      applyTheme: (theme) => {
        const html = document.documentElement
        
        if (theme === 'dark') {
          html.classList.add('dark')
        } else {
          html.classList.remove('dark')
        }
      },

      resetSettings: () => {
        set({ settings: defaultSettings, error: null })
        get().applyTheme(defaultSettings.theme)
      },
    }),
    {
      name: 'settings-storage',
      partialize: (state) => ({
        settings: state.settings,
      }),
      // Auto-apply theme on rehydration
      onRehydrateStorage: () => (state) => {
        if (state?.settings.theme) {
          state.applyTheme(state.settings.theme)
        }
      },
    }
  )
)

// Store is ready